import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import tz from "dayjs/plugin/timezone";
import customParseFormat from "dayjs/plugin/customParseFormat";
import isBetween from "dayjs/plugin/isBetween";
import advancedFormat from "dayjs/plugin/advancedFormat";
import {
  CAMPAIGN_DATE_TIME_FORMAT,
  CAMPAIGN_STATUS,
} from "../helpers/constants";

dayjs.extend(utc);
dayjs.extend(tz);
dayjs.extend(customParseFormat);
dayjs.extend(isBetween);
dayjs.extend(advancedFormat);
const userTimezone = dayjs.tz.guess();

export const convertSnakeToTitleCase = (str) => {
  return str
    .split("-")
    .map((word) => {
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
};

export const filterCategories = (categories, topics) => {
  return categories.reduce((result, category) => {
    const parentCategory = category.wpParent?.node?.slug?.split("-")[0];
    if (topics.includes(parentCategory)) {
      return result.add(parentCategory).add(category.slug);
    }
    return result;
  }, new Set());
};

// In wordpress, level tag can be beginner-th and beginner
export const normalizeLevel = (tag) => tag.split("-")[0];

export const DIFFICULTY_LEVELS = ["beginner", "intermediate", "advanced"];
export const filterDifficultyTags = (tags) => {
  for (const tag of tags) {
    const normalizedTag = normalizeLevel(tag.slug);
    if (DIFFICULTY_LEVELS.includes(normalizedTag)) {
      return normalizedTag;
    }
  }
  return undefined;
};

export const getCampaignStatus = (campaignDate, instanceTimezone) => {
  const campaignStartDate = dayjs(
    campaignDate.campaignStartDate,
    CAMPAIGN_DATE_TIME_FORMAT
  )
    .tz(instanceTimezone, true)
    .tz(userTimezone);
  const campaignEndDate = dayjs(
    campaignDate.campaignEndDate,
    CAMPAIGN_DATE_TIME_FORMAT
  )
    .tz(instanceTimezone, true)
    .tz(userTimezone);

  if (dayjs().isBetween(campaignStartDate, campaignEndDate)) {
    return CAMPAIGN_STATUS.LIVE;
  }
  if (dayjs().isBefore(campaignStartDate)) {
    return CAMPAIGN_STATUS.COMING_SOON;
  }
  return CAMPAIGN_STATUS.ENDED;
};

export const isShowSupportCategory = (categories, lang) => {
  const country = lang.split("_")[0];
  return (
    !categories ||
    categories?.includes("all") ||
    categories?.includes(country === "en" ? "global" : country)
  );
};

export const isTranslationEmpty = (t, key) => {
  return t(key) === key;
};

export const getAcfPartialContent = (partial) =>
  partial?.edges[0]?.node?.acfPartials || null;

export const formatAcfPartial = (data) => {
  const partial = getAcfPartialContent(data);

  if (!partial) return null;

  return {
    headerHTML: partial.pHeading,
    headerClassNames: partial.pCssClasses,
    contentHTML: partial.pContent,
    signupBtnLabel: partial.pButtonText,
    signupBtnLinkOverride: partial.pButtonUrl,
    imgURL: partial.pIcon?.localFile?.publicURL || "",
  };
};

export const getObjectValByString = (str, obj) =>
  str.split(".").reduce((p, c) => (p && p[c]) || null, obj);

export const getPathnameWithoutRegion = (pathname) =>
  pathname.replace(/(\/(th|id)\/(en\/)?|\/au\/)/, "/");

export const getZlaunchCoinWPInfo = (wpCoinsInfoItems, coinSlug) => {
  const coinWPInfo = wpCoinsInfoItems.edges.filter((coin) => {
    return coin.node.acfCoinInfo.ciTickerSymbol === coinSlug;
  });
  if (coinWPInfo.length > 0) {
    return {
      excerpt: coinWPInfo[0].node.excerpt,
      icon: coinWPInfo[0].node.acfCoinInfo.ciIcon.localFile.publicURL,
      slug: coinWPInfo[0].node.slug,
    };
  }
  return "";
};

export const formatNumberToLocale = (value, locale, digit = 0) =>
  parseFloat(value).toLocaleString(locale, { minimumFractionDigits: digit });

export const getTimezoneByInstance = (instance) =>
  ({
    global: {
      tz: "Asia/Singapore",
      offset: dayjs().tz("Asia/Singapore").format("z"),
    },
    au: {
      tz: "Australia/Sydney",
      offset: dayjs().tz("Australia/Sydney").format("z"),
    },
    id: { tz: "Asia/Jakarta", offset: dayjs().tz("Asia/Jakarta").format("z") },
    th: { tz: "Asia/Bangkok", offset: dayjs().tz("Asia/Bangkok").format("z") },
  }[instance]);
